/**
 * Reports dashboard section: tabbed list with create/edit collapse.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ReportsDataTable } from '@/components/reports/inperson/ReportsDataTable';
import { EditReportView } from '@/components/reports/inperson/EditReportView';
import { CreateReportView } from '@/components/reports/inperson/CreateReportView';
import { SelectReportTypeModal } from '@/components/reports/inperson/SelectReportTypeModal';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { Button } from '@/components/forms/Button';
import type { ElementWidth } from '@/types/interface-elements.types';
import { type ReportInPersonTab as Tab } from '@/components/reports/report-configs';
import { toReportInPersonTab as toTab } from '@/components/reports/inperson/report-table-utils';

/** Props for {@link ReportsSection}. */
interface ReportsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'edit' | 'create' | null;

/** Dashboard widget: in-person reports list with create/edit. */
export const ReportsSection: React.FC<ReportsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const { canCreate, creatableTypes: creatableTypesRaw } = usePageActions('reports');
  const { elements } = useInterfaceElements('reports');
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editReportData, setEditReportData] = React.useState<{
    sessionId: string;
    reportId: string;
    sessionType?: string;
  } | null>(null);
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [selectedReportType, setSelectedReportType] = React.useState<Tab | null>(null);
  const queryClient = useQueryClient();
  const { openRowId, openRow, closeRow } = useDetailsView();
  const sectionRef = React.useRef<HTMLDivElement>(null);

  /** Close the form and drop edit / create-type state. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditReportData(null);
    setSelectedReportType(null);
  }, []);

  /**
   * Types creatable from action-create-* RBAC elements (per-type).
   * Falls back to allowedTabIds when no action-create-* elements exist.
   */
  const creatableTabIds = React.useMemo((): Tab[] => {
    const TAB_IDS = new Set<Tab>(['tutoring', 'information-meeting']);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is Tab => TAB_IDS.has(id as Tab));
    }
    // Fallback: derive from tab elements
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    const otherTabs: Tab[] =
      tabElements.length === 0
        ? ['tutoring', 'information-meeting']
        : tabElements.map((el) => toTab(el.elementId)).filter((id): id is Tab => id !== null);
    return otherTabs;
  }, [creatableTypesRaw, elements]);

  const handleCreateClick = React.useCallback(() => {
    if (creatableTabIds.length === 1) {
      setSelectedReportType(creatableTabIds[0]);
      setActiveForm('create');
      setTypeModalOpen(false);
      closeRow();
    } else {
      setTypeModalOpen(true);
      setEditReportData(null);
      closeRow();
    }
  }, [creatableTabIds, closeRow]);

  const handleTypeSelect = (type: Tab) => {
    setSelectedReportType(type);
    setActiveForm('create');
    setTypeModalOpen(false);
    closeRow();
  };

  const handleEditReport = (sessionId: string, reportId: string, sessionType?: string) => {
    setEditReportData({ sessionId, reportId, sessionType });
    setActiveForm('edit');
    closeRow();
  };

  /** Scroll the section to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  /** Close the form and refresh in-person report queries. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditReportData(null);
    setSelectedReportType(null);
    void queryClient.invalidateQueries({ queryKey: ['reports'] });
    onSuccess?.();
  }, [queryClient, onSuccess]);

  /** Pick the collapse-wrapper form from `activeForm`. */
  const formContent = React.useMemo(() => {
    if (activeForm === 'edit' && editReportData) {
      return (
        <EditReportView
          key={editReportData.reportId}
          sessionId={editReportData.sessionId}
          reportId={editReportData.reportId}
          sessionType={editReportData.sessionType}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    if (activeForm === 'create' && selectedReportType) {
      return (
        <CreateReportView
          reportType={selectedReportType}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    return null;
  }, [activeForm, editReportData, selectedReportType, closeForm, handleFormSuccess]);

  return (
    <div ref={sectionRef}>
      <CollapseReplaceWrapper mode="replace" isOpen={activeForm !== null} formContent={formContent}>
        <div className="space-y-3">
          {canCreate && (
            <div className="flex justify-end">
              <Button preset="create-entity" size="sm" onClick={handleCreateClick}>
                Créer un compte rendu
              </Button>
            </div>
          )}
          <ReportsDataTable
            isSection={true}
            layoutWidth={layoutWidth}
            expandedRowId={openRowId}
            onToggleRow={openRow}
            onCloseDetails={closeRow}
            onEditReport={handleEditReport}
          />
        </div>
      </CollapseReplaceWrapper>

      <SelectReportTypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelect={handleTypeSelect}
        allowedTabIds={creatableTabIds}
      />
    </div>
  );
};
